<template>
  <div class="space-y-6">
    <!-- Order Details Card -->
    <AppCard title="Order Details">
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <span class="text-navy-500 text-sm">File Number</span>
          <span class="text-navy-800 text-sm font-medium">
            {{ fetchedOrder.FileNumber }}
          </span>
        </div>
        <div
          v-if="fetchedOrder.Property"
          class="flex items-center justify-between"
        >
          <span class="text-navy-500 text-sm">Property</span>
          <span class="text-navy-800 text-sm font-medium">
            {{ propertyAddress }}
          </span>
        </div>
        <div
          v-if="fetchedOrder.ClosingDate"
          class="flex items-center justify-between"
        >
          <span class="text-navy-500 text-sm">Closing Date</span>
          <span class="text-navy-800 text-sm font-medium">
            {{ formatDate(fetchedOrder.ClosingDate) }}
          </span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-navy-500 text-sm">ID Verification</span>
          <StatusBadge
            :variant="verificationEnabled ? 'success' : 'neutral'"
            :label="verificationEnabled ? 'Enabled' : 'Disabled'"
          />
        </div>
        <div class="flex items-center justify-between">
          <span class="text-navy-500 text-sm">Document Email</span>
          <StatusBadge
            :variant="documentEmailEnabled ? 'success' : 'neutral'"
            :label="documentEmailEnabled ? 'Enabled' : 'Disabled'"
          />
        </div>
      </div>
    </AppCard>

    <!-- Parties Card -->
    <AppCard>
      <template #header>
        <button
          class="flex w-full cursor-pointer items-center justify-between"
          @click="partiesExpanded = !partiesExpanded"
        >
          <div class="flex items-center gap-2">
            <h3 class="text-navy-800 text-sm font-semibold">Parties</h3>
            <span class="text-navy-400 text-xs">
              {{ selectedParties.length }} selected
            </span>
          </div>
          <i
            :class="[
              'pi text-navy-400 text-[10px] transition-transform',
              partiesExpanded ? 'pi-chevron-up' : 'pi-chevron-down',
            ]"
          />
        </button>
      </template>

      <div v-if="partiesExpanded">
        <div
          v-if="selectedParties.length === 0"
          class="text-navy-400 py-2 text-sm"
        >
          No parties selected
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="(party, index) in selectedParties"
            :key="index"
            class="flex items-center justify-between rounded-md bg-neutral-50 px-3 py-2"
          >
            <span class="text-navy-700 text-sm">{{ party.FullName }}</span>
            <StatusBadge
              :variant="roleBadge(party.Role).variant"
              :label="roleBadge(party.Role).label"
            />
          </div>
        </div>
      </div>
    </AppCard>

    <!-- Documents Card -->
    <AppCard>
      <template #header>
        <button
          class="flex w-full cursor-pointer items-center justify-between"
          @click="documentsExpanded = !documentsExpanded"
        >
          <div class="flex items-center gap-2">
            <h3 class="text-navy-800 text-sm font-semibold">Documents</h3>
            <span class="text-navy-400 text-xs">
              {{ selectedDocuments.length }} selected
            </span>
          </div>
          <i
            :class="[
              'pi text-navy-400 text-[10px] transition-transform',
              documentsExpanded ? 'pi-chevron-up' : 'pi-chevron-down',
            ]"
          />
        </button>
      </template>

      <div v-if="documentsExpanded">
        <div
          v-if="selectedDocuments.length === 0"
          class="text-navy-400 py-2 text-sm"
        >
          No documents selected
        </div>
        <div v-else class="space-y-1.5">
          <div
            v-for="doc in selectedDocuments"
            :key="doc.document_id"
            class="flex items-center justify-between rounded-md bg-neutral-50 px-3 py-2"
          >
            <div class="flex items-center gap-2">
              <i class="pi pi-file-pdf text-navy-300 text-sm" />
              <span class="text-navy-700 text-sm">{{ doc.file_name }}</span>
            </div>
            <span class="text-navy-400 text-xs">
              {{ formatSize(doc.size_bytes) }}
            </span>
          </div>
          <p class="text-navy-400 mt-2 text-xs">
            Documents will be imported in the background after the order is
            created.
          </p>
        </div>
      </div>
    </AppCard>

    <!-- Officer Assignment -->
    <AppCard title="Closing Officer">
      <div>
        <select
          v-model="selectedOfficerId"
          class="text-navy-800 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none"
        >
          <option v-for="member in members" :key="member.id" :value="member.id">
            {{ member.name }}
          </option>
        </select>
      </div>
    </AppCard>

    <!-- Navigation footer -->
    <div class="flex items-center justify-between">
      <button
        class="text-navy-500 hover:text-navy-700 cursor-pointer text-sm transition-colors"
        @click="$emit('back')"
      >
        &larr; Back to documents
      </button>
      <AppButton :loading="isSubmitting" @click="handleSubmit">
        Create Order
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue"

import type {
  FetchedDocument,
  FetchedOrderData,
  FetchedOrderParty,
  MemberNode,
} from "../../pages/Order/types"
import AppButton from "../ui/AppButton.vue"
import AppCard from "../ui/AppCard.vue"
import StatusBadge from "../ui/StatusBadge.vue"

const roleMap: Record<
  FetchedOrderParty["Role"],
  { variant: "blue" | "purple" | "info" | "neutral"; label: string }
> = {
  Buyer: { variant: "blue", label: "Buyer" },
  Seller: { variant: "purple", label: "Seller" },
  SettlementAgent: { variant: "info", label: "Settlement Agent" },
  ListingAgentBroker: { variant: "neutral", label: "Listing Agent" },
  SellingAgentBroker: { variant: "neutral", label: "Selling Agent" },
  Lender: { variant: "info", label: "Lender" },
}

const roleBadge = (role: FetchedOrderParty["Role"]) =>
  roleMap[role] ?? { variant: "neutral" as const, label: role }

const props = defineProps<{
  fetchedOrder: FetchedOrderData
  selectedParties: FetchedOrderParty[]
  selectedDocuments: FetchedDocument[]
  verificationEnabled: boolean
  documentEmailEnabled: boolean
  members: MemberNode[]
}>()

const emit = defineEmits<{
  back: []
  submit: [data: { officerId: string }]
}>()

const partiesExpanded = ref(true)
const documentsExpanded = ref(true)
const isSubmitting = ref(false)
const selectedOfficerId = ref(props.members[0]?.id || "")

const propertyAddress = (() => {
  const p = props.fetchedOrder.Property
  if (!p) return ""
  return [p.Street, p.City, p.State, p.ZipCode].filter(Boolean).join(", ")
})()

const formatDate = (iso: string): string => {
  const date = new Date(iso)
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const handleSubmit = () => {
  isSubmitting.value = true
  emit("submit", { officerId: selectedOfficerId.value })
}
</script>
